import { ComponentProps } from "react";
import { Controller } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { Input } from "../../Atoms/Inputs/Input";
import { SimpleSelect } from "../../Atoms/Selects/SimpleSelect";
import { MessageErroFormV2 } from "../../Atoms/MessageErroFormV2";
import { ISelectOptions } from "../../../types/inputs";
import {
  IFormDeliveryResidenceV2Values,
  useFormDeliveryResidenceV2,
} from "./useFormDeliveryResidenceV2";

interface IFormDeliveryResidenceV2Props extends ComponentProps<"form"> {
  onSubmitForm: (data: IFormDeliveryResidenceV2Values) => void;
}

export const FormDeliveryResidenceV2 = ({
  onSubmitForm,
  className,
  ...rest
}: IFormDeliveryResidenceV2Props) => {
  const {
    control,
    errors,
    handleSubmit,
    register,
    watch,
    cidades,
    ufOptions,
    handleCep,
  } = useFormDeliveryResidenceV2();

  return (
    <form
      {...rest}
      onSubmit={handleSubmit(onSubmitForm)}
      className={twMerge(
        "w-full mx-auto grid grid-cols-2 sm:grid-cols-6 gap-x-4 gap-y-8",
        className,
      )}
    >
      <div className="col-span-2 sm:col-span-6">
        <Input
          id="nome"
          data-error={!!errors?.nome}
          {...register("nome")}
          label="Nome"
          required
        />
        {errors?.nome && (
          <MessageErroFormV2>{errors.nome.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-2 sm:col-span-3">
        <Input
          id="telefone"
          data-error={!!errors?.telefone}
          {...register("telefone")}
          label="Telefone"
          required
          maxLength={15}
        />
        {errors?.telefone && (
          <MessageErroFormV2>{errors.telefone.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-2 sm:col-span-3">
        <Input
          id="cep"
          data-error={!!errors?.endereco?.cep}
          {...register("endereco.cep")}
          maxLength={9}
          label="CEP"
          onBlur={handleCep}
          required
        />
        {errors?.endereco?.cep && (
          <MessageErroFormV2>{errors.endereco.cep.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-2 sm:col-span-6">
        <Input
          id="rua"
          data-error={!!errors?.endereco?.logradouro}
          {...register("endereco.logradouro")}
          label="Endereço (Rua)"
          required
        />
        {errors?.endereco?.logradouro && (
          <MessageErroFormV2>
            {errors.endereco.logradouro.message}
          </MessageErroFormV2>
        )}
      </div>

      <div className="col-span-1 sm:col-span-3">
        <Input
          id="numero"
          data-error={!!errors?.endereco?.numero}
          {...register("endereco.numero")}
          label="Número"
          required
        />
        {errors?.endereco?.numero && (
          <MessageErroFormV2>{errors.endereco.numero.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-1 sm:col-span-3">
        <Input
          id="complemento"
          data-error={!!errors?.endereco?.complemento}
          {...register("endereco.complemento")}
          label="Complemento"
        />
        {errors?.endereco?.complemento && (
          <MessageErroFormV2>
            {errors.endereco.complemento.message}
          </MessageErroFormV2>
        )}
      </div>

      <div className="col-span-2 sm:col-span-2">
        <Input
          id="bairro"
          data-error={!!errors?.endereco?.bairro}
          {...register("endereco.bairro")}
          label="Bairro"
          required
        />
        {errors?.endereco?.bairro && (
          <MessageErroFormV2>{errors.endereco.bairro.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-1 sm:col-span-2">
        <Controller
          control={control}
          name="endereco.uf"
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              id="uf"
              label="UF"
              required
              data-error={!!errors?.endereco?.uf}
              value={ufOptions.find((i) => i.value === value) || null}
              options={ufOptions}
              onChange={(e: ISelectOptions) => onChange(e?.value)}
            />
          )}
        />
        {errors?.endereco?.uf && (
          <MessageErroFormV2>{errors.endereco.uf.message}</MessageErroFormV2>
        )}
      </div>

      <div className="col-span-1 sm:col-span-2">
        <Controller
          control={control}
          name="endereco.cidade"
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              key={watch("endereco.uf")}
              label="Cidade"
              required
              data-error={!!errors?.endereco?.cidade}
              options={cidades}
              value={cidades.find((e) => e.value === value) || null}
              onChange={(e: ISelectOptions) => onChange(e?.value)}
            />
          )}
        />
        {errors?.endereco?.cidade && (
          <MessageErroFormV2>{errors.endereco.cidade.message}</MessageErroFormV2>
        )}
      </div>
    </form>
  );
};
